import { useCallback,useEffect,useRef,useState } from 'react'
import { get } from '../lib/api'
import Icon from '../components/Icon'
import { getLocale,mt,type MarketKey } from '../lib/i18n'
import Character,{ type CharacterId } from './Characters'
import { chirp } from './PersonalityPick'
import type { AvatarData } from './Avatar'
import { personalityById } from '../../shared/market/personalities'

interface Wallet{balance:number;recent:{kind:string;delta:number;at:number;why:string}[]}
const L=(p:{en:string;es:string})=>getLocale()==='en'?p.en:p.es
const SEEN='cl.tok.seen'
const readSeen=()=>{try{return Number(localStorage.getItem(SEEN)||0)}catch{return 0}}
const writeSeen=(n:number)=>{try{localStorage.setItem(SEEN,String(n))}catch{/* shown again next time */}}

/** The person's animal at the top of the main pages: it talks in its own voice, hops when tapped, and shows the token balance. */
export function BuddyBanner({avatar,page}:{avatar:AvatarData;page:string}){
 const p=personalityById(avatar.personality),[n,setN]=useState(0),[hop,setHop]=useState(false),[bal,setBal]=useState<number|null>(null)
 const load=useCallback(()=>{get<Wallet>('/api/market/wallet').then(w=>setBal(w.balance)).catch(()=>{/* the balance stays hidden */})},[])
 useEffect(()=>{load();window.addEventListener('avatar:changed',load);return()=>window.removeEventListener('avatar:changed',load)},[load])
 useEffect(()=>{if(!p)return;const t=setInterval(()=>setN(x=>(x+1)%p.lines.length),15000);return()=>clearInterval(t)},[p])
 if(!p)return null
 const poke=()=>{setN(x=>(x+1)%p.lines.length);setHop(true);setTimeout(()=>setHop(false),700);chirp(p.id)}
 const hint=mt(`buddyHint_${page==='' ? 'browse' : page}` as MarketKey)
 return <aside className="buddy-banner" aria-label={L(p.title)}>
  <button type="button" className={`buddy-art${hop?' hop':''}`} aria-label={`${L(p.title)}. ${mt('buddyTap')}`} onClick={poke} onPointerEnter={()=>chirp(p.id,true)}><Character id={avatar.character as CharacterId} look={avatar.look} size={92}/></button>
  <div className="bb-say"><strong>{L(p.title)}</strong><p role="status">{L(p.lines[n]!)}</p><small>{hint}</small></div>
  <a className="bb-coins" href="#/avatar"><span className="bb-bal"><Icon name="star" size={16}/>{bal??'-'}</span><span>{mt('buddyDress')}</span></a>
 </aside>
}

/** A small cheer when tokens arrive, wherever the person is. It reads the wallet, so every way of earning is covered. */
export function Cheer({avatar}:{avatar:AvatarData|null}){
 const [toast,setToast]=useState<{text:string}|null>(null),hide=useRef<ReturnType<typeof setTimeout>>(undefined)
 const p=personalityById(avatar?.personality)
 const check=useCallback(()=>{
  get<Wallet>('/api/market/wallet').then(w=>{
   const seen=readSeen()||Date.now()-120000
   const fresh=w.recent.filter(r=>r.kind!=='spend'&&r.delta>0&&r.at>seen)
   // Remember the newest ledger row the server showed, not the browser's clock, so the two can never disagree.
   writeSeen(Math.max(seen,...w.recent.map(r=>r.at)))
   if(!fresh.length)return
   const total=fresh.reduce((s,r)=>s+r.delta,0),reasons=[...new Set(fresh.map(r=>mt(`cheer_${r.why}` as MarketKey)))].join(', ')
   setToast({text:`${mt('cheerTokens').replace('{n}',String(total))}: ${reasons}`})
   if(p)chirp(p.id,true)
   clearTimeout(hide.current);hide.current=setTimeout(()=>setToast(null),7000)
   window.dispatchEvent(new Event('avatar:changed'))
  }).catch(()=>{/* try again next time */})
 },[p])
 useEffect(()=>{
  if(!avatar)return
  const first=setTimeout(check,1800),every=setInterval(check,90000)
  window.addEventListener('focus',check);window.addEventListener('tokens:check',check)
  return()=>{clearTimeout(first);clearInterval(every);window.removeEventListener('focus',check);window.removeEventListener('tokens:check',check)}
 },[avatar,check])
 if(!toast||!avatar)return null
 return <div className="cheer" role="status" onClick={()=>setToast(null)}><span className="cheer-art"><Character id={avatar.character as CharacterId} look={avatar.look} size={68}/></span><p>{toast.text}</p></div>
}
